import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KIM_INPUT_CHARACTER_LIMIT, KimInput } from './kim-input';
import { useKimFileUpload } from '../hooks/use-kim-file-upload';
import { EditableTemplate, TemplateBuilderDialog } from '../template-builder/template-builder-dialog';
import { TemplatesPopover } from '../templates/templates-popover';
import { WEXLER_PROMPT_TEMPLATES } from '../templates/wexler-prompt-templates';
import { KIM_FILE_INPUT_ACCEPT } from '../utils/upload-constants';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateKimThread, type KimThreadSource } from '@/api/mutations/useCreateKimThread';
import { resetNewThreadWarmThrottle } from '@/api/mutations/useWarmKim';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { DocMentionsInputHandle, extractKimMentions } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { SubsetChip } from '@/new/components/subset-chip';
import { cn } from '@/new/lib/utils';

interface NewKimLargeInputProps {
  matterId?: string;
  subsetId?: string;
  headingText?: string;
  placeholder?: string;
  kimInputClassName?: string;
  threadSource?: KimThreadSource;
}

export interface NewKimLargeInputHandle {
  prefill: (text: string) => void;
}

export const NewKimLargeInput = forwardRef<NewKimLargeInputHandle, NewKimLargeInputProps>(function NewKimLargeInput(
  {
    matterId: matterIdProp,
    subsetId: subsetIdProp,
    headingText,
    placeholder,
    kimInputClassName,
    threadSource,
  }: NewKimLargeInputProps,
  ref,
) {
  const params = useParams();
  const matterId = matterIdProp ?? params.matterId;
  const subsetId = subsetIdProp ?? params.subsetId;
  const navigate = useNavigate();
  const location = useLocation();
  const isOnFullKimPage = location.pathname.includes('/new-kim');
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [isTemplateBuilderOpen, setIsTemplateBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EditableTemplate | null>(null);
  const [templateInitialDraft, setTemplateInitialDraft] = useState<{ title: string; body: string } | undefined>(
    undefined,
  );
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const inputRef = useRef<DocMentionsInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const rawQuestionFromSearch = next.get('question');
    const shouldAutoSubmit = next.get('autoSubmit') === 'true';

    if (rawQuestionFromSearch) {
      next.delete('question');
      next.delete('autoSubmit');
      setSearchParams(next, { replace: true });
      setQuestion(rawQuestionFromSearch);
      if (shouldAutoSubmit) setPendingAutoSubmit(true);
      return;
    }

    const templateId = next.get('template');
    if (templateId) {
      const template = WEXLER_PROMPT_TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setQuestion(template.template);
      }

      next.delete('template');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.has('question') || searchParams.has('template')) return;

    const questionFromState = (location.state as { question?: string } | null)?.question?.trim();
    if (questionFromState) {
      setQuestion(questionFromState);
    }
  }, []);

  const resolvedThreadSource: KimThreadSource = threadSource ?? (isOnFullKimPage ? 'kim_page' : 'matter_overview');
  const { mutate: createThread, isLoading } = useCreateKimThread(matterId, subsetId, resolvedThreadSource);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const suggestedQuestions = useMemo(() => {
    const questions = subset?.exampleKimQuestions?.question?.map((q) => q.prompt) ?? [];
    const drafts = subset?.exampleKimQuestions?.draft?.map((q) => q.prompt) ?? [];
    return [...questions, ...drafts];
  }, [subset]);

  const handleTemplateSelect = (body: string) => {
    setQuestion(body);
    inputRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({ prefill: handleTemplateSelect }), []);

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed || isUploading || trimmed.length > KIM_INPUT_CHARACTER_LIMIT) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;

    createThread(
      { message: trimmed, files, mentions: extractKimMentions(trimmed) },
      {
        onSuccess: (data) => {
          resetNewThreadWarmThrottle();
          clearFiles();
          navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${data.id}`);
        },
        onError: () => {
          toast.error('Error creating thread');
        },
      },
    );
  };

  useEffect(() => {
    if (!pendingAutoSubmit) return;
    if (!question.trim() || question.trim().length > KIM_INPUT_CHARACTER_LIMIT || isUploading || isLoading) return;
    setPendingAutoSubmit(false);
    handleSubmit();
  }, [pendingAutoSubmit, question, isUploading, isLoading]);

  return (
    <div className="mb-12 flex w-full max-w-xl flex-col items-center">
      <div className="mb-12 flex flex-col items-center">
        <div className="-mt-8 mb-6 flex h-6 items-center">
          {subsetId && <SubsetChip subsetId={subsetId} className="bg-white" />}
        </div>

        <h2 className="font-serif text-2xl font-medium">{headingText ?? 'Ask Kim...'}</h2>
        {!isOnFullKimPage && (
          <Link
            to={`/matters/${matterId}/subsets/${subsetId}/new-kim`}
            className="group mt-1.5 flex items-center justify-center gap-1 text-center text-xs text-gray-400 duration-150 hover:text-gray-600"
          >
            Go to the main Kim page{' '}
            <Icon className="lucide--chevron-right transition-transform duration-150 group-hover:translate-x-px" />
          </Link>
        )}
      </div>

      <div className="mb-1.5 flex w-full justify-between">
        <div className="flex flex-wrap">
          <Button variant={'ghost'} size={'sm'} onClick={() => inputRef.current?.triggerMention()}>
            <Icon className="lucide--at-sign" /> Mention
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={'sm'}
                disabled={isLoading || isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon className="lucide--paperclip" />
                Attach files
              </Button>
            </TooltipTrigger>
            <TooltipContent className="w-65 rounded-lg p-2">
              <div className="relative h-24 overflow-hidden rounded-md border bg-gray-50">
                <div className="absolute top-2.5 left-5.5 scale-95 rounded-lg border border-gray-300 bg-gray-100 p-2 whitespace-nowrap shadow-lg/3">
                  <p className="mb-1.5 px-1 py-px">
                    Formatted like this document, draft a witness statement for John Doe.
                  </p>
                  <div className="flex w-fit items-center gap-1 rounded-md border bg-white px-1.5 py-0.75">
                    <Icon className="material-icon-theme--word text-xl text-blue-400" />
                    <div className="-space-y-0.5">
                      <p>example-witness-st...</p>
                      <p className="text-[11px] font-light text-gray-400">DOCX</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-1.25 pt-2.5 pb-0.5">
                <p className="mb-1 font-medium text-gray-900">Attach files in Kim</p>
                <p className="text-gray-600">
                  Try uploading a template to capture its formatting, or search for inconsistencies against a relevant{' '}
                  {t('witness statement')}.
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={KIM_FILE_INPUT_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) addFiles?.(files);
            e.target.value = '';
          }}
        />

        <div className="flex flex-wrap justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'} className="pr-0">
                Suggested Questions
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="group/suggestions -mt-1.5 max-h-90 max-w-md space-y-1.5 bg-gray-100 p-1.5"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {suggestedQuestions.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs">
                  No suggested questions
                </DropdownMenuItem>
              ) : (
                <>
                  <p className="px-1.5 py-px text-xs text-gray-400">
                    <strong>{suggestedQuestions.length}</strong> suggested question
                    {suggestedQuestions.length === 1 ? '' : 's'}
                  </p>
                  {suggestedQuestions.map((prompt, index) => (
                    <DropdownMenuItem
                      key={index}
                      className="bg-white px-2.25 py-1.5 whitespace-normal text-gray-700 hover:bg-white! group-has-[:hover]/suggestions:[&:not(:hover)]:text-gray-600 group-has-[:hover]/suggestions:[&:not(:hover)]:opacity-80"
                      onClick={() => {
                        setQuestion(prompt);
                        inputRef.current?.focus();
                      }}
                    >
                      {prompt}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <TemplatesPopover
            onSelect={handleTemplateSelect}
            onOpenTemplateBuilder={() => setIsTemplateBuilderOpen(true)}
            onEditTemplate={setEditingTemplate}
          />
        </div>
      </div>

      <KimInput
        ref={inputRef}
        matterId={matterId}
        subsetId={subsetId}
        className={cn('max-h-[50vh] min-h-28 w-full p-4 py-3 pb-0', kimInputClassName)}
        placeholder={placeholder ?? 'What is this Matter about?'}
        value={question}
        onChange={setQuestion}
        onSubmit={handleSubmit}
        disabled={isLoading}
        uploadedFiles={uploadedFiles}
        isUploading={isUploading}
        onFilesAdded={addFiles}
        onFileRemove={removeFile}
        onSaveAsTemplate={() => {
          setTemplateInitialDraft({ title: '', body: question });
          setIsTemplateBuilderOpen(true);
        }}
        autoFocus
      />

      <TemplateBuilderDialog
        open={isTemplateBuilderOpen || editingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsTemplateBuilderOpen(false);
            setEditingTemplate(null);
            setTemplateInitialDraft(undefined);
          } else {
            setIsTemplateBuilderOpen(true);
          }
        }}
        template={editingTemplate ?? undefined}
        initialDraft={templateInitialDraft}
      />

      <div className="mt-3 flex flex-wrap items-center justify-center gap-1 gap-y-1.5">
        {WEXLER_PROMPT_TEMPLATES.map((template) => (
          <Button
            key={template.id}
            variant="white"
            size="sm"
            className="rounded-xl px-2.5 py-1.5 font-normal text-gray-700 hover:bg-white hover:text-gray-800"
            onClick={() => {
              trackCustomEvent(TrackingEventsV2.KimUseTemplate, {
                matterId,
                templateSource: 'wexler',
                template: template.id,
                source: 'composer_chip',
              });
              handleTemplateSelect(template.template);
            }}
          >
            <Icon className={template.icon} />
            {template.label}
          </Button>
        ))}
      </div>
    </div>
  );
});
